import {
  AlertStatus,
  Box,
  BoxProps,
  Button,
  CloseButton,
  Text,
  ToastId,
  useToast,
} from "@chakra-ui/react";
import { captureException } from "@sentry/nextjs";
import { useEffect, useRef } from "react";
import { asyncVoid } from "utils/asyncVoidHandler";
import { useActiveAppAlerts } from "utils/currentUserHooks";
import { LocalStorageKeyJSON } from "utils/localStorage";
import {
  AlertStatus as AlertStatusEnum,
  AppAlertPlacement,
} from "__generated__/gql/graphql";

async function getRenderedBody(body: string | null | undefined) {
  if (!body) return;

  const renderMarkdownModule =
    await import("components/Shared/MarkdownText/render");
  return renderMarkdownModule.default(body);
}

const statusMap: Record<AlertStatusEnum, AlertStatus> = {
  [AlertStatusEnum.ERROR]: "error",
  [AlertStatusEnum.INFO]: "info",
  [AlertStatusEnum.SUCCESS]: "success",
  [AlertStatusEnum.WARNING]: "warning",
};

const statusAccentColorMap: Record<AlertStatusEnum, string> = {
  [AlertStatusEnum.ERROR]: "red.400",
  [AlertStatusEnum.INFO]: "blue.400",
  [AlertStatusEnum.SUCCESS]: "green.400",
  [AlertStatusEnum.WARNING]: "orange.400",
};

function DismissAlertFooter({
  onDismiss,
  ...props
}: BoxProps & { onDismiss: () => void }) {
  return (
    <Box textAlign="right" {...props}>
      <Button variant="link" size="xs" onClick={onDismiss}>
        Don’t show again
      </Button>
    </Box>
  );
}

export default function useAppAlerts() {
  const alerts = useActiveAppAlerts();

  const toastIdsRef = useRef<ToastId[]>([]);

  const lsRef = useRef(new LocalStorageKeyJSON<string[]>("dismissedAppAlerts"));

  const dismissedAppAlertsRef = useRef(lsRef.current.get() || []);

  const showToast = useToast();

  useEffect(() => {
    if (alerts) {
      try {
        const toastIds = toastIdsRef.current;
        const dismissedAppAlerts = dismissedAppAlertsRef.current;

        const alertIds = alerts.map((alert) => {
          if (
            !toastIds.includes(alert.id) &&
            !dismissedAppAlerts.includes(alert.id)
          ) {
            asyncVoid(
              getRenderedBody(alert.body).then((renderedBody) => {
                const closeAlert = () => showToast.close(alert.id);

                const dismissAlert = () => {
                  dismissedAppAlerts.push(alert.id);
                  lsRef.current.set(dismissedAppAlerts);
                  closeAlert();
                };

                if (alert.placement === AppAlertPlacement.BOTTOM_LEFT) {
                  const accentColor =
                    alert.status === AlertStatusEnum.INFO
                      ? "gray.400"
                      : statusAccentColorMap[alert.status];

                  const toastId = showToast({
                    position: "bottom-left",
                    duration: null,
                    variant: "subtle",
                    containerStyle: {
                      width: "20rem",
                      maxWidth: "calc(100vw - 2rem)",
                    },
                    id: alert.id,
                    render: () => (
                      <Box
                        position="relative"
                        borderLeftWidth="4px"
                        borderLeftColor={accentColor}
                        borderRadius="md"
                        bg="white"
                        boxShadow="lg"
                        px={4}
                        py={3}
                        pr={10}
                      >
                        <CloseButton
                          position="absolute"
                          top={3}
                          right={3}
                          onClick={closeAlert}
                        />

                        {alert.heading ? (
                          <Text fontSize="sm" fontWeight="semibold" mb={2}>
                            {alert.heading}
                          </Text>
                        ) : null}

                        <Box fontSize="sm">{renderedBody || alert.body}</Box>

                        <DismissAlertFooter mt={3} onDismiss={dismissAlert} />
                      </Box>
                    ),
                  });

                  if (toastId) toastIds.push(toastId);
                  return;
                }

                const toastId = showToast({
                  position: "top",
                  duration: null,
                  isClosable: true,
                  variant: "subtle",
                  containerStyle: { width: "50rem", maxWidth: "75vw" },
                  id: alert.id,
                  title: alert.heading,
                  description: (
                    <>
                      {renderedBody || alert.body}

                      <DismissAlertFooter onDismiss={dismissAlert} />
                    </>
                  ),
                  status: statusMap[alert.status],
                });

                if (toastId) toastIds.push(toastId);
              }),
            );
          }

          return alert.id;
        });

        toastIds.forEach((toastId) => {
          if (typeof toastId === "string" && !alertIds.includes(toastId)) {
            showToast.close(toastId);
          }
        });
      } catch (e) {
        captureException(e);
      }
    }
  }, [alerts, showToast]);
}
